import React from 'react';
import { Card, CardContent } from './ui/card';
import { Award, Users, Target, CheckCircle } from 'lucide-react';

export const About = () => {
  const achievements = [
    {
      icon: Award,
      title: "Experienced HR Professionals",
      description: "15+ years of hands-on HR experience supporting businesses of all sizes, from startups to 150+ employee teams."
    },
    {
      icon: Users,
      title: "Multi-Industry Expertise",
      description: "Real-world HR background across 12+ industries, including technology, manufacturing, healthcare, retail, and professional services."
    },
    {
      icon: Target,
      title: "Proven HR Results",
      description: "Supported hundreds of employees through hiring, onboarding, compliance, performance, and day-to-day HR operations."
    }
  ];

  const values = [
    {
      title: "We Make HR Make Sense",
      description: "While others overwhelm you with jargon, we cut straight to what matters. Clear steps, real solutions, and zero fluff, because HR shouldn't feel like decoding a legal textbook."
    },
    {
      title: "Your Strategic HR Partner",
      description: "We don't just hand you templates. We give you the structure, direction, and confidence to run HR like a pro. When you need guidance, we're the team you want in your corner."
    },
    {
      title: "Built for Today's Workplace",
      description: "DriveHR delivers modern, digital-first tools that match the speed of today's businesses. No outdated forms. No corporate clutter. Just clean, powerful resources that work."
    },
    {
      title: "Smart HR for Every Industry",
      description: "Whether you're scaling a startup, running a service business, or managing a growing team, we understand the realities of your industry, and our solutions are built to support them."
    }
  ];

  return (
    <section id="about" className="py-14 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            About DriveHR
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your HR Partner, Not Another Corporate Consultant
          </p>
        </div>

        {/* Main About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Text Content */}
          <div>
            
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                We get it. You started your business to solve problems, serve customers, and grow a team, not to spend hours decoding HR laws or building policies from scratch. That's where DriveHR comes in.
              </p>
              <p>
                DriveHR was founded on a simple belief: small and midsize businesses deserve HR support that's practical, affordable, and built for the real world. No corporate jargon. No one-size-fits-all playbooks. Just clear guidance and ready-to-use tools that help you run HR with confidence.
              </p>
              <p>
                Whether you're a manufacturing company navigating employee relations issues, a tech startup preparing your first employee handbook, or a growing retail business working to reduce turnover, we speak your language and understand your challenges. Our resources are designed to meet you where you are and help you move forward with clarity and ease.
              </p>
              <p>
                At DriveHR, we're more than templates. We're your strategic partner in building a stronger, more compliant, and people-focused workplace.
              </p>
            </div>
          </div>

          {/* Achievements Cards */}
          <div className="space-y-6">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="bg-blue-100 rounded-full p-3 flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{achievement.title}</h3>
                      <p className="text-gray-600">{achievement.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Our Values */}
        <div>
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              What Makes Us Different
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not your typical HR consultants. Here's how we approach things differently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-green-100 rounded-full p-2 flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">{value.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-20 bg-white rounded-2xl p-8 md:p-12 text-center shadow-lg">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Our Mission
          </h3>
          <div className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed space-y-4">
            <p>
              To empower small and midsize businesses with accessible, modern HR tools that simplify compliance, strengthen workplace culture, and support confident decision making. At DriveHR, we serve as your trusted HR partner, providing the resources, guidance, and expertise you need to build a thriving workplace without the cost of a full HR department.
            </p>
            <p className="font-medium text-blue-600 text-xl">
              We're here to drive your HR forward, together.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};